import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { podShapeKeys, solutionPodPositionKeys } from "@/lib/query";

export type Composition = "Solo" | "Pair" | "Trio" | "Quad";

export type PodShape = {
  id: string;
  name: string;
  composition: Composition;
  disciplineR1Id: string;
  disciplineR1Code: string;
  disciplineR2Id: string | null;
  disciplineR2Code: string | null;
  disciplineR3Id: string | null;
  disciplineR3Code: string | null;
  disciplineR4Id: string | null;
  disciplineR4Code: string | null;
  active: boolean;
  isTierDefaultFor: string[];
  referenced: boolean;
};

export type TierWithDefaultShape = {
  id: string;
  name: string;
  defaultShapeId: string | null;
};

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export function usePodShapes(opts: { includeInactive?: boolean } = {}) {
  return useQuery({
    queryKey: podShapeKeys.list(opts),
    queryFn: () =>
      jsonFetch<PodShape[]>(
        `/api/pod-shapes${opts.includeInactive ? "?includeInactive=true" : ""}`,
      ),
  });
}

export function useTiersWithDefaultShape() {
  return useQuery({
    queryKey: podShapeKeys.tiers(),
    queryFn: () => jsonFetch<TierWithDefaultShape[]>("/api/engagement-standard-tiers"),
  });
}

export function useCreatePodShape() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      composition: Composition;
      disciplineR1Id: string;
      disciplineR2Id: string | null;
      disciplineR3Id: string | null;
      disciplineR4Id: string | null;
    }) =>
      jsonFetch<PodShape>("/api/pod-shapes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: podShapeKeys.all });
      toast.success("Shape created");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdatePodShape() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      name?: string;
      active?: boolean;
      composition?: Composition;
      disciplineR1Id?: string;
      disciplineR2Id?: string | null;
      disciplineR3Id?: string | null;
      disciplineR4Id?: string | null;
    }) =>
      jsonFetch<PodShape>(`/api/pod-shapes/${input.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...input, id: undefined }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: podShapeKeys.all });
      // Shape discipline edits re-derive position rows on read.
      qc.invalidateQueries({ queryKey: solutionPodPositionKeys.all });
      toast.success("Shape updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useSetTierDefaultShape() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { tierId: string; shapeId: string }) =>
      jsonFetch<TierWithDefaultShape>(
        `/api/engagement-standard-tiers/${input.tierId}/default-shape`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ shapeId: input.shapeId }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: podShapeKeys.all });
      toast.success("Tier default updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
